/* =============================================================
   ROUTES
   =============================================================
   Every path in the admin panel, in one object.

   Static paths live in `routes`; anything with an :id is a
   pattern for the router and a builder for links, kept apart so
   no component ever interpolates a URL by hand.

   Order matters in the router for `/berths/new` vs `/berths/:id`
   — Vue Router ranks static segments above dynamic ones, but
   the detail routes also reject a non-numeric id, so "new" can
   never fall through to the detail page by accident.
   ============================================================= */

export const routes = {
  signIn: '/sign-in',

  dashboard: '/dashboard',

  berths: '/berths',
  berthNew: '/berths/new',

  vessels: '/vessels',
  vesselNew: '/vessels/new',

  companies: '/companies',
  companyNew: '/companies/new',

  bookings: '/bookings',
} as const

/** Router patterns. Not for linking — use `to` below. */
export const patterns = {
  berthDetail: '/berths/:id(\\d+)',
  berthEdit: '/berths/:id(\\d+)/edit',
  berthRateNew: '/berths/:id(\\d+)/rates/new',
  berthRateEdit: '/berths/:id(\\d+)/rates/:rateId(\\d+)/edit',
  berthBlockNew: '/berths/:id(\\d+)/blocks/new',
  vesselDetail: '/vessels/:id(\\d+)',
  vesselEdit: '/vessels/:id(\\d+)/edit',
  companyDetail: '/companies/:id(\\d+)',
  companyEdit: '/companies/:id(\\d+)/edit',
  bookingDetail: '/bookings/:id(\\d+)',
} as const

/** Link builders for the parameterised routes. */
export const to = {
  berth: (id: number) => `/berths/${id}`,
  berthEdit: (id: number) => `/berths/${id}/edit`,
  /** A berth page opened on one of its tabs. 'details' is the default, so no query. */
  berthTab: (id: number, tab: 'details' | 'rates' | 'availability' | 'bookings') =>
    tab === 'details' ? `/berths/${id}` : `/berths/${id}?tab=${tab}`,
  berthRateNew: (id: number) => `/berths/${id}/rates/new`,
  berthRateEdit: (id: number, rateId: number) => `/berths/${id}/rates/${rateId}/edit`,
  berthBlockNew: (id: number) => `/berths/${id}/blocks/new`,
  vessel: (id: number) => `/vessels/${id}`,
  vesselEdit: (id: number) => `/vessels/${id}/edit`,
  company: (id: number) => `/companies/${id}`,
  companyEdit: (id: number) => `/companies/${id}/edit`,
  booking: (id: number) => `/bookings/${id}`,
} as const

export type RoutePath = (typeof routes)[keyof typeof routes]

/** Where an admin lands right after signing in, and what "/" resolves to. */
export const HOME_ROUTE: RoutePath = routes.dashboard
